import { useCallback, useMemo, useRef, useState } from 'react';

import { type PressRefusal, sameRefusal } from './pressRefusal';

/**
 * One progress button's press: idle → queued (the press is on its way) → running (the server took
 * it, the work runs) → idle; or refused, with the reason the bar shows next to the button.
 */
export type PressPhase = 'idle' | 'queued' | 'running' | 'refused';

interface PressState {
  phase: PressPhase;
  refusal: PressRefusal | null;
}

export interface PressFeedback extends PressState {
  /** Claim a press. False while one is queued or running: a second press is ignored, never sent. */
  begin: () => boolean;
  /** The server accepted the press; its work runs. */
  accept: () => void;
  /** Nothing started: keep the reason on screen and let the user press again. */
  refuse: (refusal: PressRefusal) => void;
  /** Back to idle: the work finished, the press was withdrawn, or its refusal no longer holds. */
  reset: () => void;
}

const IDLE: PressState = { phase: 'idle', refusal: null };

/**
 * The claim is a ref, not state: two clicks in one frame both see the same render, and only the
 * first may send. The phase is state set inside the click handler, so the queued label paints in
 * the same frame as the press.
 */
export const usePressFeedback = (): PressFeedback => {
  const [state, setState] = useState<PressState>(IDLE);
  const busyRef = useRef(false);
  const begin = useCallback(() => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setState({ phase: 'queued', refusal: null });

    return true;
  }, []);
  const accept = useCallback(() => {
    busyRef.current = true;
    setState({ phase: 'running', refusal: null });
  }, []);
  const refuse = useCallback((refusal: PressRefusal) => {
    busyRef.current = false;
    setState((current) => (
      current.phase === 'refused' && sameRefusal(current.refusal, refusal) ? current : { phase: 'refused', refusal }
    ));
  }, []);
  const reset = useCallback(() => {
    busyRef.current = false;
    setState(IDLE);
  }, []);

  return useMemo(() => ({ ...state, begin, accept, refuse, reset }), [state, begin, accept, refuse, reset]);
};
